import React from 'react';
import PropTypes from 'prop-types';
import theme from 'Theme';
import classNames from 'classnames';
import withStyles from '@material-ui/core/styles/withStyles';
import { Button, IconButton } from '@material-ui/core';
import { AddCircle } from '@material-ui/icons';
import CalendarIcon from 'Assets/Images/timeline/timeline-calendar.svg';
import FileIcon from 'Assets/Images/timeline/file.svg';
import Tag from 'Assets/Images/timeline/tag.svg';
import GmailIcon from 'Assets/Images/timeline/gmail.png';
import GoalIcon from 'Assets/Images/timeline/goal.svg';
import NotesIcon from 'Assets/Images/timeline/notes.svg';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import TimelineEvent from '../Event';

const styles = stylesTheme => ({
  root: {
    paddingBottom: theme.spacing(3),
  },
  addEventButton: {
    color: stylesTheme.palette.facet.accentGreen,
  },
  buttonContainer: {
    display: 'flex',
  },
  buttonSpacer: {
    width: '160px',
    margin: stylesTheme.spacing(3),
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  loadMoreButton: {
    color: stylesTheme.palette.facet.accentGreen,
  },
  noBorder: {
    '& div:last-child': {
      borderBottom: 'none',
    },
  },
  event: {}, // Adding for overrides from parents
});

const EVENT_CATEGORY = {
  user: {
    icon: 'user',
    backgroundColor: '#687488',
  },
  escalation: {
    icon: 'user',
    backgroundColor: '#862020',
  },
  fileUpload: {
    icon: FileIcon,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.primaryBlue,
  },
  path: {
    icon: 'road',
    backgroundColor: '#53B4E5',
  },
  note: {
    icon: NotesIcon,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.accentBlue,
  },
  call: {
    icon: 'phone',
    backgroundColor: '#38997B',
  },
  email: {
    icon: GmailIcon,
    useCustomIcon: true,
    backgroundColor: theme.palette.common.white,
  },
  otherEmail: {
    icon: GmailIcon,
    useCustomIcon: true,
    backgroundColor: theme.palette.common.white,
  },
  calendarEvent: {
    icon: CalendarIcon,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.primaryBlue,
  },
  agenda: {
    icon: CalendarIcon,
    useCustomIcon: true,
    backgroundColor: '#B18EF3',
  },
  task: {
    icon: 'check-square',
    backgroundColor: theme.palette.facet.accentBlue,
  },
  kissflow: {
    icon: 'check-square-o',
    backgroundColor: '#53b4e5',
  },
  goal: {
    icon: GoalIcon,
    useCustomIcon: true,
    backgroundColor: '#38997b',
  },
  riskToleranceAssessment: {
    icon: FileIcon,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.accentBlue,
  },
  rolloverRecommendationQuestionnaire: {
    icon: FileIcon,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.accentBlue,
  },
  couponAdded: {
    icon: Tag,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.accentBlue,
  },
  couponRemoved: {
    icon: Tag,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.accentBlue,
  },
  couponAppliedToInvoice: {
    icon: Tag,
    useCustomIcon: true,
    backgroundColor: theme.palette.facet.accentBlue,
  },
};

const getKey = e => `${e.id}${e.name}${e.entTime}${e.category}${e.entKey}${e.status_id}`;

const TimelineEvents = ({
  timelineEvents,
  classes,
  className,
  onEventClick,
  onAddEvent,
  selectedEvent,
  onLoadMore,
  loading,
  endOfTimelineEvents,
}) => {
  const loadMoreButtonText = endOfTimelineEvents ? 'End of Results' : 'Load More';

  if (loading) {
    return (
      <LoadingIndicator />
    );
  }
  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.buttonContainer}>
        <div className={classes.buttonSpacer} />
        <IconButton onClick={onAddEvent} className={classes.addEventButton}>
          <AddCircle />
        </IconButton>
        <div />
      </div>
      {timelineEvents.map((event, index) => {
        const eve = event;
        const cat = EVENT_CATEGORY[eve.category];
        if (cat) {
          eve.backgroundColor = cat.backgroundColor;
          eve.icon = cat.icon;
        }

        return (
          <TimelineEvent
            key={getKey(event)}
            onClick={() => onEventClick(event)}
            useCustomIcon={!!cat.useCustomIcon}
            selected={selectedEvent && (getKey(event) === getKey(selectedEvent))}
            className={classNames(classes.event, {
              [classes.noBorder]: index === timelineEvents.length - 1,
            })}
            {...eve}
          />
        );
      })}
      <div className={classes.loadMoreContainer}>
        <Button
          className={classes.loadMoreButton}
          color="primary"
          size="large"
          startIcon={<AddCircle />}
          onClick={() => onLoadMore()}
          disabled={endOfTimelineEvents}
        >
          {loadMoreButtonText}
        </Button>
      </div>
    </div>
  );
};

export default withStyles(styles)(TimelineEvents);

TimelineEvents.propTypes = {
  timelineEvents: PropTypes.array,
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  onEventClick: PropTypes.func.isRequired,
  onAddEvent: PropTypes.func.isRequired,
  selectedEvent: PropTypes.object,
  onLoadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  endOfTimelineEvents: PropTypes.bool.isRequired,
};

TimelineEvents.defaultProps = {
  timelineEvents: [],
  className: '',
  selectedEvent: null,
  loading: false,
};
